// ============================================================================
// ProfileSummaryHeader (L16, F-825..F-827) — header sommaire de la fiche profil
// ============================================================================
// FRERE de TeamSummaryHeader / CommitteeSummaryHeader / PostingSummaryHeader
// (gabarit nav fiche, standard permanent UI_STANDARDS §Pattern fiche) : tuile
// h-11 (44px, post-BL-19a) + titre + badges role/comite + ligne de coordonnees +
// responsable. Tokens @gs/ui uniquement (regle #28) — zero valeur CSS en dur.
//
// AVATAR = INITIALES (contrainte GEST-STORAGE-MIGRATION, inchangee depuis E1c) :
// `profile_image_path` pointe le bucket Firebase NON migre (et vaut NULL 139/139
// en prod), `google_avatar_url` est une URL Google CDN externe (CSP + fuite de
// referrer + duree de vie non maitrisee). On rend donc getInitials (M-859) —
// c'est le fallback NATIF du lot, pas un pis-aller.
//
// F-826 : matricule / equipe / date d'arrivee sont chacun masques si absents — la
// DECISION de les fournir est prise au CALLSITE (et deja appliquee par le serveur :
// matricule null si !canManageEmployee, hireDate absent si !canReadCompensation).

import { CalendarDays, Hash, Mail, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import {
  personRoleI18nKey,
  type DerivedPersonRole,
} from '../../lib/org/derivePersonRole';
import { getInitials } from '../../lib/profile/initials';

function asDerivedRole(role: string): DerivedPersonRole {
  if (role === 'admin' || role === 'manager' || role === 'director' || role === 'employee') {
    return role;
  }
  return 'employee';
}

/** F-825 : badge de role (AFFICHAGE seul — jamais une garde d'acces). */
const ROLE_BADGE: Record<string, string> = {
  admin: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  manager: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
  director: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
  employee: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

export interface ProfileSummaryHeaderProps {
  fullName: string | null;
  email: string;
  /** Role metier EFFECTIF de la personne affichee (badge F-825). */
  role: string;
  /** 'ce' | 'cg' | null — badge comite (attribut metier, PAS un role). */
  committee: string | null;
  jobTitle: string | null;
  teamName: string | null;
  /** F-814 : deja null si le viewer n'a pas le droit (decide serveur). */
  matricule: string | null;
  /** F-815 : deja null si le viewer n'a pas canReadCompensation (decide serveur). */
  hireDate: string | null;
  responsable: { email: string; name: string | null } | null;
  eyebrow: string;
  /** Slot d'actions d'en-tete (AvatarForm dans le PoC — DIFFERE ici, cf. blob). */
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

export function ProfileSummaryHeader({
  fullName, email, role, committee, jobTitle, teamName,
  matricule, hireDate, responsable, eyebrow, actions, children,
}: ProfileSummaryHeaderProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('en') ? 'en-CA' : 'fr-CA';

  // hire_date = 'YYYY-MM-DD' interprete en UTC (parite PoC : sinon decalage d'un
  // jour selon le fuseau du navigateur).
  const hired = (() => {
    if (!hireDate) return null;
    const d = new Date(hireDate);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    }).format(d);
  })();

  const meta: Array<{ key: string; icon: React.ReactNode; content: string }> = [
    { key: 'email', icon: <Mail size={14} aria-hidden="true" />, content: email },
  ];
  if (matricule) {
    meta.push({ key: 'matricule', icon: <Hash size={14} aria-hidden="true" />, content: t('gest:profile.header.matricule', { value: matricule }) });
  }
  if (teamName) {
    meta.push({ key: 'team', icon: <Users size={14} aria-hidden="true" />, content: teamName });
  }
  if (hired) {
    meta.push({ key: 'hired', icon: <CalendarDays size={14} aria-hidden="true" />, content: t('gest:profile.header.hiredOn', { date: hired }) });
  }

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-start gap-3 px-3.5 py-3">
        {/* Tuile h-11/44px (BL-19a) — initiales, jamais un avatar distant. */}
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-field font-bold text-brand-600 dark:bg-brand-950 dark:text-brand-400">
          {getInitials(fullName, email)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">{eyebrow}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            {/* h1 unique de la page (parite PoC F-825). */}
            <h1 className="truncate text-head font-bold text-slate-900 dark:text-slate-50">
              {fullName ?? email}
            </h1>
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-chip font-medium ${ROLE_BADGE[role] ?? ROLE_BADGE.employee}`}>
              {t(personRoleI18nKey(asDerivedRole(role)))}
            </span>
            {committee ? (
              <span className="inline-flex items-center rounded-full border border-slate-200 px-2.5 py-0.5 text-chip font-medium text-slate-500 dark:border-slate-600 dark:text-slate-400">
                {t(`gest:profile.committees.${committee}`)}
              </span>
            ) : null}
          </div>
          {jobTitle ? (
            <p className="mt-0.5 truncate text-meta text-slate-500 dark:text-slate-400">{jobTitle}</p>
          ) : null}
          <ul className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-meta text-slate-500 dark:text-slate-400">
            {meta.map((item) => (
              <li key={item.key} className="flex min-w-0 items-center gap-1.5">
                {item.icon}
                <span className="truncate">{item.content}</span>
              </li>
            ))}
          </ul>
          {/* F-827 : lien Responsable -> sa fiche. */}
          {responsable ? (
            <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">
              {t('gest:profile.header.managerLabel')}{' '}
              <Link
                to={`/profile/${encodeURIComponent(responsable.email)}`}
                className="touch-manipulation font-medium text-slate-700 underline-offset-4 transition-colors hover:text-brand-600 hover:underline dark:text-slate-200"
              >
                {responsable.name ?? responsable.email}
              </Link>
            </p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {children ? (
        <div className="border-t border-slate-200 px-3.5 py-3 dark:border-slate-700">{children}</div>
      ) : null}
    </div>
  );
}
